// /care/start
//
// Public guided-care entry. Three ways to begin: portal (in-page form),
// WhatsApp (deep-link to the patient-facing Twilio number), or request a
// callback (same form, call mode). All routes feed the same care case so
// the recommended next step is consistent.
//
// IMPORTANT: copy here is strictly user-facing. Internal language like
// "decision engine", "orchestration", "guided intake", "low friction" etc.
// has been deliberately kept out — it leaks our internal model into the
// page and makes the surface feel like product spec text.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { APP_CONSTANTS } from 'src/consumer/constants'
import {
  completeCareIntake,
  startCareIntake,
} from 'src/consumer/services/careOrchestrationService'

import {
  Card,
  Container,
  Eyebrow,
  IconBadge,
  Lede,
  SectionTitle,
  SecondaryButton,
} from './sections'
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  MessageCircle,
  PhoneCall,
  Repeat,
  ShieldCheck,
  UserCheck,
  Workflow,
} from './icons'

const ENTRY_PORTAL = 'portal'
const ENTRY_CALL = 'call'

const SUBJECT_OPTIONS = [
  { value: 'SELF', label: 'For me' },
  { value: 'CHILD', label: 'For my child' },
  { value: 'DEPENDENT', label: 'For someone I care for' },
]

const CONCERN_OPTIONS = [
  { value: 'ANXIETY_LOW_MOOD', label: 'Feeling anxious, low, or overwhelmed' },
  { value: 'SLEEP_BURNOUT', label: 'Sleep, fatigue, burnout, or stress' },
  { value: 'MEDICATION_EVALUATION', label: 'May need medical evaluation or medication' },
  { value: 'CHILD_BEHAVIOUR', label: 'Child behaviour, school, attention, or development' },
  { value: 'THERAPY_NOT_HELPING', label: 'Tried therapy, not improving enough' },
  { value: 'UNSURE', label: 'Not sure what is happening' },
]

const PRIOR_SUPPORT_OPTIONS = [
  { value: 'NONE', label: 'No prior support' },
  { value: 'THERAPY', label: 'Therapy before' },
  { value: 'MEDICATION', label: 'Medication before' },
  { value: 'BOTH', label: 'Therapy and medication' },
]

const SUPPORT_OPTIONS = [
  { value: 'THERAPY', label: 'Therapy' },
  { value: 'DOCTOR_CONSULTATION', label: 'Doctor consultation' },
  { value: 'HELP_DECIDING', label: 'Help me decide' },
  { value: 'REQUEST_CALL', label: 'A callback first' },
]

const URGENCY_OPTIONS = [
  { value: 'LOW', label: 'Whenever fits' },
  { value: 'HIGH', label: 'I would like help soon' },
  { value: 'IMMEDIATE_DANGER', label: 'Immediate safety concern' },
]

const MODE_OPTIONS = [
  { value: 'ONLINE', label: 'Online' },
  { value: 'IN_PERSON', label: 'In-person if available' },
  { value: 'WHATSAPP', label: 'Continue on WhatsApp' },
  { value: 'CALL', label: 'Phone call' },
]

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
]

const WHATSAPP_PREFILL = 'Hi, I would like help finding the right care.'

function buildPrefilledWhatsappUrl(baseUrl, message) {
  const separator = baseUrl.includes('?') ? '&' : '?'
  return `${baseUrl}${separator}text=${encodeURIComponent(message)}`
}

function normalisePhone(rawValue) {
  return rawValue.replace(/[^\d+]/g, '').trim()
}

function cleanOptional(value) {
  const trimmed = value.trim()
  return trimmed ? trimmed : undefined
}

function buildStartPayload(form, selectedEntry) {
  const entryIsCall = selectedEntry === ENTRY_CALL
  return {
    name: cleanOptional(form.name),
    phone: normalisePhone(form.phone),
    email: cleanOptional(form.email),
    preferredLanguage: form.preferredLanguage,
    city: cleanOptional(form.city),
    consentGiven: form.consentGiven,
    source: 'WEB',
    channel: entryIsCall ? 'CALL' : 'WEB',
    subjectType: form.subjectType,
    presentingConcern: form.presentingConcern,
    concernDetails: cleanOptional(form.concernDetails),
    priorSupportHistory: form.priorSupportHistory,
    supportPreference: entryIsCall ? 'REQUEST_CALL' : form.supportPreference,
    urgencyAnswer: form.urgencyAnswer,
    preferredMode: entryIsCall ? 'CALL' : form.preferredMode,
    preferredCallbackTime: cleanOptional(form.preferredCallbackTime),
  }
}

// Map the backend's nextActionType to the right user-facing CTA. We keep
// the label in plain language ('Book a session', 'Continue on WhatsApp')
// instead of mirroring backend enums.
function getRecommendedButton(outcome, fallbackWhatsappUrl) {
  const nextActionType = outcome?.nextActionType
  if (nextActionType === 'BOOK_PSYCHIATRY' || nextActionType === 'BOOK_THERAPY') {
    return {
      href: outcome?.bookingLink || '/therapy-psychiatry',
      label:
        nextActionType === 'BOOK_PSYCHIATRY'
          ? 'Book a consultation'
          : 'Book a therapy session',
      external: !!(outcome?.bookingLink && /^https?:\/\//i.test(outcome?.bookingLink)),
    }
  }
  if (nextActionType === 'CONTINUE_WHATSAPP') {
    return {
      href: outcome?.humanSupportWhatsappUrl || fallbackWhatsappUrl,
      label: 'Continue on WhatsApp',
      external: true,
    }
  }
  if (nextActionType === 'COORDINATOR_CALLBACK') {
    return {
      href: APP_CONSTANTS.SUPPORT_SCHEDULE_CALL,
      label: 'Pick a time for the call',
      external: true,
    }
  }
  if (nextActionType === 'ESCALATE_URGENT') {
    return {
      href: outcome?.humanSupportWhatsappUrl || fallbackWhatsappUrl,
      label: 'Reach our care team now',
      external: true,
    }
  }
  return null
}

// Translate backend codes into plain words. Anything not in the map is
// shown as-is so we don't accidentally hide useful detail.
const URGENCY_DISPLAY = {
  LOW: 'Standard',
  HIGH: 'Soon',
  IMMEDIATE_DANGER: 'Urgent',
}
const STATUS_DISPLAY = {
  INTAKE_IN_PROGRESS: 'In progress',
  INTAKE_COMPLETED: 'Ready for next step',
  TRIAGE_GENERATED: 'Recommendation ready',
  ASSIGNED: 'With our care team',
  CLOSED: 'Closed',
}

function humanise(value, dictionary) {
  if (!value) return null
  return dictionary[value] || value
}

export default function CareStartView() {
  const intakeSectionRef = useRef(null)
  const urlParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  )
  const [selectedEntry, setSelectedEntry] = useState(
    urlParams.get('mode') === ENTRY_CALL ? ENTRY_CALL : ENTRY_PORTAL,
  )
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    subjectType: 'SELF',
    presentingConcern: 'UNSURE',
    concernDetails: '',
    priorSupportHistory: 'NONE',
    supportPreference: 'HELP_DECIDING',
    urgencyAnswer: 'LOW',
    city: '',
    preferredLanguage: 'en',
    preferredMode: 'ONLINE',
    preferredCallbackTime: '',
    consentGiven: true,
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [outcome, setOutcome] = useState(null)

  // Patient-facing WhatsApp number — same number the server uses for
  // inbound patient WA conversations (twilioWhatsappFromPhoneNumberForPatients).
  const whatsappUrl = useMemo(
    () => buildPrefilledWhatsappUrl(APP_CONSTANTS.PATIENT_CARE_WHATSAPP_LINK, WHATSAPP_PREFILL),
    [],
  )

  useEffect(() => {
    if (selectedEntry === ENTRY_CALL) {
      setForm((current) => ({
        ...current,
        supportPreference: 'REQUEST_CALL',
        preferredMode: 'CALL',
      }))
      return
    }
    setForm((current) => ({
      ...current,
      supportPreference:
        current.supportPreference === 'REQUEST_CALL'
          ? 'HELP_DECIDING'
          : current.supportPreference,
      preferredMode: current.preferredMode === 'CALL' ? 'ONLINE' : current.preferredMode,
    }))
  }, [selectedEntry])

  function focusIntake(mode) {
    setSelectedEntry(mode)
    setOutcome(null)
    setError('')
    window.requestAnimationFrame(() => {
      intakeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  function updateField(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setSubmitting(true)
    setError('')
    setOutcome(null)

    try {
      const startPayload = buildStartPayload(form, selectedEntry)
      const started = await startCareIntake(startPayload)
      if (!started?.caseId) {
        throw new Error('We could not start your request. Please try again.')
      }
      const completedOutcome = await completeCareIntake(started.caseId)
      if (!completedOutcome) {
        throw new Error('We could not generate a next step yet. Our team will follow up.')
      }
      setOutcome(completedOutcome)
      window.requestAnimationFrame(() => {
        intakeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    } catch (submitError) {
      setError(
        submitError?.message ||
          'Something went wrong. Please try again, or message us on WhatsApp.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const cta = getRecommendedButton(outcome, whatsappUrl)
  const isCallEntry = selectedEntry === ENTRY_CALL

  return (
    <main id="main">
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="as-care-hero">
        <Container>
          <div className="as-grid-12 as-items-center">
            <div className="as-col-7">
              <Eyebrow>Get help</Eyebrow>
              <h1 className="as-h1">
                How can we help you{' '}
                <span className="as-brand-gradient">today?</span>
              </h1>
              <Lede>
                Tell us a little about what&rsquo;s going on. We&rsquo;ll suggest the right
                next step &mdash; therapy, a clinical consultation, or a coordinator who can
                walk you through it &mdash; and you decide what to do.
              </Lede>
              <div className="as-mt-6">
                <Link to="/care/get-started" className="as-btn as-btn-primary">
                  Get matched with a specialist <ArrowRight />
                </Link>
              </div>
              <ul className="as-mt-6 as-flex as-wrap as-gap-6" aria-label="What this means">
                <li className="as-trust-item">
                  <ShieldCheck /> Confidential
                </li>
                <li className="as-trust-item">
                  <UserCheck /> Reviewed by our care team
                </li>
                <li className="as-trust-item">
                  <Repeat /> No commitment to book
                </li>
              </ul>
              <div className="as-care-note">
                If you or someone you know is in immediate danger, please contact local
                emergency services right away.
              </div>
            </div>
            <div className="as-col-5">
              <Card>
                <div className="as-flex as-items-center as-gap-3">
                  <IconBadge Icon={Workflow} />
                  <div>
                    <p className="as-care-kicker">What happens next</p>
                    <h3 className="as-h3">A short reply, usually the same day</h3>
                  </div>
                </div>
                <ul className="as-check-list as-care-check-list">
                  <li>
                    <CheckCircle2 />
                    We review what you shared
                  </li>
                  <li>
                    <CheckCircle2 />
                    We share a clear next step
                  </li>
                  <li>
                    <CheckCircle2 />
                    You choose how to move forward
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Three ways to begin (no internal-flavoured bullets) ── */}
      <section className="as-section as-section-white">
        <Container>
          <div className="as-max-w-3">
            <Eyebrow>Choose how to start</Eyebrow>
            <SectionTitle>Three ways to begin</SectionTitle>
          </div>
          <div className="as-care-entry-grid as-mt-12">
            <button
              type="button"
              className={`as-care-entry-card${selectedEntry === ENTRY_PORTAL ? ' as-care-entry-card--selected' : ''}`}
              onClick={() => focusIntake(ENTRY_PORTAL)}
            >
              <div className="as-flex as-items-center as-gap-3">
                <IconBadge Icon={ClipboardList} />
                <h3 className="as-h3">Continue here</h3>
              </div>
              <p className="as-p as-mt-3">
                Answer a few quick questions and see your suggested next step.
              </p>
              <span className="as-link as-link-violet as-care-entry-cta">
                Continue here <ArrowRight />
              </span>
            </button>

            <a
              href={whatsappUrl}
              className="as-care-entry-card"
              target="_blank"
              rel="noreferrer"
            >
              <div className="as-flex as-items-center as-gap-3">
                <IconBadge Icon={MessageCircle} />
                <h3 className="as-h3">WhatsApp us</h3>
              </div>
              <p className="as-p as-mt-3">
                Message our care team on WhatsApp and reply when it suits you.
              </p>
              <span className="as-link as-link-violet as-care-entry-cta">
                Open WhatsApp <ArrowRight />
              </span>
            </a>

            <button
              type="button"
              className={`as-care-entry-card${selectedEntry === ENTRY_CALL ? ' as-care-entry-card--selected' : ''}`}
              onClick={() => focusIntake(ENTRY_CALL)}
            >
              <div className="as-flex as-items-center as-gap-3">
                <IconBadge Icon={PhoneCall} variant="slate" />
                <h3 className="as-h3">Get a call back</h3>
              </div>
              <p className="as-p as-mt-3">
                Share your number and a good time. A coordinator will call you.
              </p>
              <span className="as-link as-link-violet as-care-entry-cta">
                Request a callback <ArrowRight />
              </span>
            </button>
          </div>
        </Container>
      </section>

      {/* ── Form ─────────────────────────────────────────── */}
      <section ref={intakeSectionRef} className="as-section as-section-alt">
        <Container>
          <div className="as-grid-12">
            <div className="as-col-5">
              <Eyebrow>{isCallEntry ? 'Request a callback' : 'Tell us a little'}</Eyebrow>
              <SectionTitle>
                {isCallEntry
                  ? 'A coordinator will call you back'
                  : 'A few quick questions'}
              </SectionTitle>
              <Lede>
                {isCallEntry
                  ? 'Share your number and a good time to call. We\u2019ll keep this short.'
                  : 'There are no wrong answers. The more you share, the better we can suggest a next step.'}
              </Lede>
              <div className="as-care-aside-card">
                <div className="as-flex as-items-center as-gap-3">
                  <IconBadge Icon={ShieldCheck} variant="slate" />
                  <div>
                    <p className="as-care-kicker">Your privacy</p>
                    <h3 className="as-h3">Used only to set up your care</h3>
                  </div>
                </div>
                <p className="as-p as-mt-3">
                  We use what you share to suggest the right care and to follow up. We
                  don&rsquo;t share it with anyone outside our care team.
                </p>
                <div className="as-care-entry-actions">
                  <SecondaryButton href={whatsappUrl}>
                    Prefer WhatsApp instead?
                  </SecondaryButton>
                </div>
              </div>
            </div>

            <div className="as-col-7">
              <form className="as-care-form-panel" onSubmit={handleSubmit}>
                <div className="as-care-form-grid">
                  <label className="as-field">
                    <span className="as-field-label">Your name</span>
                    <input
                      className="as-input"
                      type="text"
                      value={form.name}
                      onChange={(event) => updateField('name', event.target.value)}
                      placeholder="First name is fine"
                    />
                  </label>

                  <label className="as-field">
                    <span className="as-field-label">Mobile number</span>
                    <input
                      className="as-input"
                      type="tel"
                      required
                      value={form.phone}
                      onChange={(event) => updateField('phone', event.target.value)}
                      placeholder="+91&hellip;"
                    />
                  </label>

                  <label className="as-field">
                    <span className="as-field-label">Email (optional)</span>
                    <input
                      className="as-input"
                      type="email"
                      value={form.email}
                      onChange={(event) => updateField('email', event.target.value)}
                      placeholder="If you'd like a written summary"
                    />
                  </label>

                  <label className="as-field">
                    <span className="as-field-label">Preferred language</span>
                    <select
                      className="as-input as-select"
                      value={form.preferredLanguage}
                      onChange={(event) => updateField('preferredLanguage', event.target.value)}
                    >
                      {LANGUAGE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="as-field as-field-full">
                    <span className="as-field-label">Who is the care for?</span>
                    <div className="as-choice-grid">
                      {SUBJECT_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`as-choice-chip${form.subjectType === option.value ? ' as-choice-chip--active' : ''}`}
                          onClick={() => updateField('subjectType', option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <label className="as-field as-field-full">
                    <span className="as-field-label">What feels closest to what&rsquo;s going on?</span>
                    <select
                      className="as-input as-select"
                      required
                      value={form.presentingConcern}
                      onChange={(event) => updateField('presentingConcern', event.target.value)}
                    >
                      {CONCERN_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="as-field as-field-full">
                    <span className="as-field-label">Anything else you&rsquo;d like to share? (optional)</span>
                    <textarea
                      className="as-input as-textarea"
                      value={form.concernDetails}
                      onChange={(event) => updateField('concernDetails', event.target.value)}
                      placeholder="A few lines about what you're experiencing or what's most difficult right now."
                    />
                  </label>

                  <label className="as-field">
                    <span className="as-field-label">Have you had support before?</span>
                    <select
                      className="as-input as-select"
                      value={form.priorSupportHistory}
                      onChange={(event) => updateField('priorSupportHistory', event.target.value)}
                    >
                      {PRIOR_SUPPORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="as-field">
                    <span className="as-field-label">City (optional)</span>
                    <input
                      className="as-input"
                      type="text"
                      value={form.city}
                      onChange={(event) => updateField('city', event.target.value)}
                      placeholder="So we can suggest in-person care if relevant"
                    />
                  </label>

                  {!isCallEntry && (
                    <div className="as-field as-field-full">
                      <span className="as-field-label">What kind of help feels closest right now?</span>
                      <div className="as-choice-grid">
                        {SUPPORT_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`as-choice-chip${form.supportPreference === option.value ? ' as-choice-chip--active' : ''}`}
                            onClick={() => updateField('supportPreference', option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="as-field as-field-full">
                    <span className="as-field-label">How urgent does this feel?</span>
                    <div className="as-choice-grid">
                      {URGENCY_OPTIONS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`as-choice-chip${form.urgencyAnswer === option.value ? ' as-choice-chip--active' : ''}`}
                          onClick={() => updateField('urgencyAnswer', option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {!isCallEntry && (
                    <label className="as-field">
                      <span className="as-field-label">How would you like to meet?</span>
                      <select
                        className="as-input as-select"
                        value={form.preferredMode}
                        onChange={(event) => updateField('preferredMode', event.target.value)}
                      >
                        {MODE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  <label className="as-field">
                    <span className="as-field-label">
                      {isCallEntry ? 'Best time to call you' : 'Best time to reach you'}
                    </span>
                    <input
                      className="as-input"
                      type="text"
                      value={form.preferredCallbackTime}
                      onChange={(event) => updateField('preferredCallbackTime', event.target.value)}
                      placeholder="Tomorrow evening, weekday mornings, etc."
                    />
                  </label>

                  <label className="as-field as-field-full as-checkbox-field">
                    <input
                      type="checkbox"
                      checked={form.consentGiven}
                      onChange={(event) => updateField('consentGiven', event.target.checked)}
                    />
                    <span>
                      I&rsquo;m okay with AUMHUM contacting me about this enquiry.
                    </span>
                  </label>
                </div>

                {error ? <div className="as-care-alert">{error}</div> : null}

                <div className="as-care-form-actions">
                  <button
                    type="submit"
                    className="as-btn as-btn-primary"
                    disabled={submitting}
                  >
                    {submitting
                      ? 'Just a moment\u2026'
                      : isCallEntry
                        ? 'Request a callback'
                        : 'See what to do next'}
                    <ArrowRight />
                  </button>
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="as-btn as-btn-secondary"
                  >
                    Message us on WhatsApp instead
                  </a>
                </div>
              </form>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Result ───────────────────────────────────────── */}
      {outcome ? (
        <section className="as-section as-section-white">
          <Container>
            <div className="as-max-w-3">
              <Eyebrow>Your suggested next step</Eyebrow>
              <SectionTitle>
                {outcome?.recommendation?.userFacingHeadline ||
                  outcome?.nextActionLabel ||
                  outcome?.recommendation?.primaryRecommendation ||
                  'Here\u2019s what we suggest'}
              </SectionTitle>
              <Lede>
                {outcome?.recommendation?.userFacingSummary ||
                  outcome?.recommendation?.rationale ||
                  'Based on what you shared, here\u2019s the next step we\u2019d suggest. You can take it now or come back to it later.'}
              </Lede>
            </div>
            <div className="as-care-result-banner as-mt-10">
              <div>
                <p className="as-care-kicker">What we suggest</p>
                <h3 className="as-h3">
                  {outcome?.nextActionLabel ||
                    outcome?.recommendation?.primaryRecommendation ||
                    'A short conversation with our care team'}
                </h3>
                <p className="as-p as-mt-2">
                  {outcome?.recommendation?.userFacingSummary ||
                    outcome?.recommendation?.rationale ||
                    'Our care team has reviewed what you shared and identified the most helpful starting point for you.'}
                </p>
              </div>
              <div className="as-care-result-actions">
                {cta ? (
                  <a
                    href={cta.href}
                    target={cta.external ? '_blank' : undefined}
                    rel={cta.external ? 'noreferrer' : undefined}
                    className="as-btn as-btn-primary"
                  >
                    {cta.label}
                    <ArrowRight />
                  </a>
                ) : null}
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="as-btn as-btn-secondary"
                >
                  Talk on WhatsApp
                </a>
              </div>
            </div>
            <div className="as-care-result-grid">
              <Card className="as-care-result-card">
                <p className="as-care-kicker">Your request</p>
                <ul className="as-check-list as-care-check-list">
                  {humanise(outcome?.careCase?.urgencyLevel, URGENCY_DISPLAY) ? (
                    <li>
                      <CheckCircle2 />
                      Urgency: {humanise(outcome?.careCase?.urgencyLevel, URGENCY_DISPLAY)}
                    </li>
                  ) : null}
                  {humanise(outcome?.careCase?.status, STATUS_DISPLAY) ? (
                    <li>
                      <CheckCircle2 />
                      Status: {humanise(outcome?.careCase?.status, STATUS_DISPLAY)}
                    </li>
                  ) : null}
                  {outcome?.careCase?.recommendedNextStep ? (
                    <li>
                      <CheckCircle2 />
                      Suggested next step: {outcome.careCase.recommendedNextStep}
                    </li>
                  ) : null}
                </ul>
              </Card>
              <Card className="as-care-result-card">
                <p className="as-care-kicker">If anything changes</p>
                <p className="as-p">
                  Reach us on WhatsApp any time and we&rsquo;ll update your plan. You don&rsquo;t
                  have to start over.
                </p>
              </Card>
            </div>
          </Container>
        </section>
      ) : null}
    </main>
  )
}
